import { LegendData } from './interfaces'
import {
  AnyLayer,
  CirclePaint,
  GeoJSONSourceRaw,
  HeatmapPaint,
} from 'mapbox-gl'

export const initialViewport = {
  longitude: 13.003211,
  latitude: 41.852713,
  zoom: 5.0,
}

export const initialLegendState: LegendData = {
  activeLayer: { id: 'limits-it-regions', label: 'Regioni' },
  ranges: {
    'limits-it-regions': [0, 0],
    'limits-it-provinces': [0, 0],
    'limits-it-municipalities': [0, 0],
    'circle-layer': [0, 0],
  },
}

export const zoomBreakpoints = [
  { label: 'Regioni', layer: 'limits-it-regions', zoom: 6.5 },
  { label: 'Province', layer: 'limits-it-provinces', zoom: 8.6 },
  { label: 'Comuni', layer: 'limits-it-municipalities', zoom: 12 },
  { label: 'Startup', layer: 'circle-layer', zoom: 22 },
]

export const mappingNomiRegione: { [index: string]: string } = {
  Abruzzo: 'ABRUZZO',
  Basilicata: 'BASILICATA',
  Calabria: 'CALABRIA',
  Campania: 'CAMPANIA',
  'Emilia-Romagna': 'EMILIA ROMAGNA',
  'Friuli-Venezia Giulia': 'FRIULI VENEZIA GIULIA',
  Lazio: 'LAZIO',
  Liguria: 'LIGURIA',
  Lombardia: 'LOMBARDIA',
  Marche: 'MARCHE',
  Molise: 'MOLISE',
  Piemonte: 'PIEMONTE',
  Puglia: 'PUGLIA',
  Sardegna: 'SARDEGNA',
  Sicilia: 'SICILIA',
  Toscana: 'TOSCANA',
  'Trentino-Alto Adige/Südtirol': 'TRENTINO ALTO ADIGE',
  Umbria: 'UMBRIA',
  "Valle d'Aosta/Vallée d'Aoste": "VALLE D'AOSTA",
  Veneto: 'VENETO',
}

export const mappingNomiComuni = {
  'appiano sulla strada del vino/eppan an der weinstraße':
    'appiano sulla strada del vino',
  'bellaria-igea marina': 'bellaria - igea marina',
  'bolzano/bozen': 'bolzano - bozen',
  'bressanone/brixen': 'bressanone',
  'brunico/bruneck': 'brunico - bruneck',
  'calatafimi-segesta': 'calatafimi segesta',
  'caldaro sulla strada del vino/kaltern an der weinstraße':
    'caldaro sulla strada del vino',
  "cassina de' pecchi": 'cassina dè pecchi',
  "cava de' tirreni": 'cava dè tirreni',
  'cavallino-treporti': 'cavallino - treporti',
  'challand-saint-anselme': 'challand - saint - anselme',
  'chiopris-viscone': 'chiopris - viscone',
  ciriè: "cirie'",
  milano: 'città metropolitana di milano',
  'cocquio-trevisago': 'cocquio - trevisago',
  'comezzano-cizzago': 'comezzano - cizzago',
  'corigliano-rossano': 'corigliano - rossano',
  cuorgnè: "cuorgne'",
  'duino aurisina-devin nabreina': 'duino - aurisina',
  'egna/neumarkt': 'egna - neumarkt',
  'falzes/pfalzen': 'falzes - pfalzen',
  'gargazzone/gargazon': 'gargazzone - gargazon',
  'guidonia montecelio': 'guidonia',
  'laion/lajen': 'laion - lajen',
  'laives/leifers': 'laives - leifers',
  'lana/lana': 'lana',
  malé: 'malè',
  'merano/meran': 'merano',
  'monrupino-repentabor': 'monrupino',
  'montecatini-terme': 'montecatini - terme',
  muggiò: "muggio'",
  'naturno/naturns': 'naturno',
  'negrar di valpolicella': 'negrar',
  'ora/auer': 'ora - auer',
  'pont-saint-martin': 'pont saint martin',
  'postal/burgstall': 'postal',
  'presicce-acquarica': 'presicce',
  'reggio di calabria': 'reggio calabria',
  "reggio nell'emilia": 'reggio emilia',
  'renon/ritten': 'renon - ritten',
  'rio di pusteria/mühlbach': 'rio di pusteria - muehlbach',
  'rodengo saiano': 'rodengo - saiano',
  'saint-christophe': 'saint - christophe',
  'saint-vincent': 'saint vincent',
  'san donà di piave': "san dona' di piave",
  'san giorgio ionico': 'san giorgio jonico',
  'san giovanni di fassa-sèn jan': 'san giovanni di fassa-sen jan',
  "sant'eufemia d'aspromonte": "sant' eufemia d'aspromonte",
  "sant'agata de' goti": "sant'agata dè goti",
  'sarentino/sarntal': 'sarentino - sarntal',
  'senale-san felice/unsere liebe frau im walde-st. felix':
    'senale - san felice',
  "serra de' conti": 'serra dè conti',
  'sesto/sexten': 'sesto - sexten',
  'sgonico-zgonik': 'sgonico',
  'termeno sulla strada del vino/tramin an der weinstraße':
    'termeno sulla strada del vino',
  "torre de' passeri": 'torre dè passeri',
  'valle di casies/gsies': 'valle di casies',
  "varano de' melegari": 'varano dè melegari',
}

export const heatmapPaint: HeatmapPaint = {
  // Increase the heatmap weight based on frequency and property magnitude
  'heatmap-weight': ['interpolate', ['linear'], ['get', 'value'], 0, 0, 6, 1],
  'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
  'heatmap-color': [
    'interpolate',
    ['linear'],
    ['heatmap-density'],
    0,
    'rgba(33,102,172,0)',
    0.2,
    'rgb(103,169,207)',
    0.4,
    'rgb(209,229,240)',
    0.6,
    'rgb(253,219,199)',
    0.8,
    'rgb(239,138,98)',
    1,
    'rgb(178,24,43)',
  ],
  // Adjust the heatmap radius by zoom level
  'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 20],
  'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 11, 1, 13, 0],
}

export const circlePaint: CirclePaint = {
  'circle-radius': [
    'interpolate',
    ['linear'],
    ['zoom'],
    13,
    ['interpolate', ['linear'], ['get', 'value'], 1, 5, 6, 9],
    22,
    ['interpolate', ['linear'], ['get', 'value'], 1, 5, 6, 50],
  ],
  // Color circle by earthquake magnitude
  'circle-color': [
    'interpolate',
    ['linear'],
    ['get', 'value'],
    1,
    'rgba(33,102,172,0)',
    2,
    'rgb(103,169,207)',
    3,
    'rgb(209,229,240)',
    4,
    'rgb(253,219,199)',
    5,
    'rgb(239,138,98)',
    6,
    'rgb(178,24,43)',
  ],
  'circle-stroke-color': 'white',
  'circle-stroke-width': 1,
  // Transition from heatmap to circle layer by zoom level
  'circle-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0, 22, 1],
}

export const heatmapSourceConfig: GeoJSONSourceRaw = {
  type: 'geojson',
  data: {
    type: 'FeatureCollection',
    features: [],
  },
}

export const heatmapLayerConfig: AnyLayer = {
  id: 'heatmap-layer',
  type: 'heatmap',
  source: 'heatmap-source',
  minzoom: 11,
  maxzoom: 13,
  paint: heatmapPaint,
}

export const circleLayerConfig: AnyLayer = {
  id: 'circle-layer',
  type: 'circle',
  source: 'heatmap-source',
  minzoom: 12,
  maxzoom: 22,
  paint: circlePaint,
}
